/**
 * SSE reader for the Synoti Brain stream endpoint (POST /api/rag/chat/stream).
 *
 * Plain fetch + ReadableStream because EventSource cannot POST or carry the
 * Authorization header — the token is injected by the global fetch wrapper in
 * services/auth.js, which passes the raw Response through untouched.
 *
 * Returns:
 *   { status: 'ok',          text, done }
 *   { status: 'unavailable' }            -> caller should use POST /api/rag/chat
 *   { status: 'error', error, httpStatus }
 */

const STREAM_URL = '/api/rag/chat/stream';

function parseSseBlock(rawEvent, dataLines, handlers) {
  if (!dataLines.length) return null;
  let payload = null;
  try {
    payload = JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }
  switch (rawEvent) {
    case 'stage':
      handlers.onStage?.(payload.stage);
      return null;
    case 'sources':
      handlers.onSources?.(payload.sources || []);
      return null;
    case 'delta':
      handlers.onDelta?.(payload.text || '');
      return null;
    case 'reasoning':
      handlers.onReasoning?.(payload.text || '');
      return null;
    case 'done':
      return { done: payload };
    case 'error':
      return { error: payload.error || 'unknown error' };
    default:
      return null;
  }
}

export async function readRagStream(payload, handlers = {}) {
  let res;
  try {
    res = await fetch(STREAM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: handlers.signal,
    });
  } catch (err) {
    return { status: 'error', error: err.message || 'network error' };
  }

  // Streaming disabled (kill switch / older backend) or route missing
  if ([404, 405, 501].includes(res.status)) {
    return { status: 'unavailable' };
  }
  if (!res.ok || !res.body) {
    let detail = `Server error (${res.status})`;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const data = await res.json();
        detail = data.detail || data.error || detail;
      }
    } catch {
      /* keep the generic message */
    }
    if (res.status >= 502) return { status: 'unavailable' };
    return { status: 'error', error: detail, httpStatus: res.status };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = 'message';
  let dataLines = [];
  let text = '';
  let done = null;
  let error = null;

  // Track the accumulated answer locally as well, so callers get `text`
  // even if they only render deltas incrementally.
  const streamHandlers = {
    ...handlers,
    onDelta: (chunk) => {
      text += chunk;
      handlers.onDelta?.(chunk);
    },
  };

  const drain = (block) => {
    if (block?.done) done = block.done;
    if (block?.error) error = block.error;
  };

  try {
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n');
      while (idx >= 0) {
        let line = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 1);
        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line === '') {
          drain(parseSseBlock(event, dataLines, streamHandlers));
          event = 'message';
          dataLines = [];
        } else if (line.startsWith('event:')) {
          event = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
        idx = buffer.indexOf('\n');
        if (error) break;
      }
      if (error) break;
    }
    if (!error && dataLines.length) drain(parseSseBlock(event, dataLines, streamHandlers));
  } catch (err) {
    if (err.name === 'AbortError') return { status: 'error', error: 'cancelled', aborted: true };
    return { status: 'error', error: err.message || 'stream read failed', partial: text };
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }

  if (error) return { status: 'error', error, partial: text };
  return { status: 'ok', text, done };
}

export default readRagStream;
